import React, { useState } from "react";
import { BACKEND_URL } from "@/lib/backendUrl";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import PasswordStrengthMeter from "@/components/PasswordStrengthMeter";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldAlert } from "lucide-react";

const API = `${BACKEND_URL}/api`;

/**
 * ForcePasswordChangeModal
 *
 * Mounted at the app root. If the currently signed-in user has
 * `must_rotate_password === true` (set by an admin emergency reset),
 * this modal opens and blocks all interaction until they set a new
 * password. Non-dismissible on purpose — a temp password must not
 * linger on the account.
 */
export default function ForcePasswordChangeModal() {
  const { user, refreshUser } = useAuth();
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [breached, setBreached] = useState(false);

  const open = !!user?.must_rotate_password;

  const submit = async (e) => {
    e.preventDefault();
    if (newPw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (newPw !== confirmPw) { toast.error("New passwords don't match"); return; }
    if (newPw === oldPw) { toast.error("New password must be different from the current one"); return; }
    if (breached) { toast.error("This password appears in known breaches — pick another."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/change-password`, { old_password: oldPw, new_password: newPw });
      toast.success("Password updated");
      setOldPw(""); setNewPw(""); setConfirmPw("");
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update password");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open}>
      <DialogContent
        data-testid="force-password-modal"
        className="bg-[#121620] border-[#EF4444]/60 text-white sm:max-w-md [&>button.absolute]:hidden"
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <ShieldAlert className="w-6 h-6 text-[#EF4444]"/>
            <DialogTitle className="font-display font-black text-2xl">Set a new password</DialogTitle>
          </div>
        </DialogHeader>
        <p className="text-sm text-[#94A3B8] pt-1">
          An admin issued a temporary password for your account. Please set your own password to continue.
        </p>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-[#94A3B8]">Temporary password</Label>
            <PasswordInput testId="force-pw-old" required minLength={6} value={oldPw} onChange={e => setOldPw(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-[#94A3B8]">New password</Label>
            <PasswordInput testId="force-pw-new" required minLength={6} value={newPw} onChange={e => setNewPw(e.target.value)} />
            <PasswordStrengthMeter password={newPw} testId="force-pw-strength" onBreachChange={setBreached}/>
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-[#94A3B8]">Confirm new password</Label>
            <PasswordInput testId="force-pw-confirm" required minLength={6} value={confirmPw} onChange={e => setConfirmPw(e.target.value)} />
          </div>
          <Button data-testid="force-pw-submit" type="submit" disabled={busy || !oldPw || !newPw || !confirmPw || breached}
            className="w-full bg-[#00F0FF] text-black hover:bg-[#33F3FF] font-black uppercase tracking-widest">
            {busy ? "…" : "Update password"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
